import React, { useState, useRef, useEffect } from 'react';
import {
  FiUsers,
  FiZap,
  FiCopy,
  FiDownload,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiAlertCircle,
  FiGlobe,
  FiInfo,
  FiFileText,
  FiFile,
  FiSearch,
  FiLoader,
} from 'react-icons/fi';
import { interviewService, contentService } from '../services/apiService';
import { useT } from '../utils/i18n';

// ─── Tab definitions ─────────────────────────────────────────────────────────

const TABS = [
  {
    id:             'interviewDoctor',
    labelKey:       'interviews.tab.doctor',
    placeholderKey: 'interviews.doctorPlaceholder',
  },
  {
    id:             'interviewEducation',
    labelKey:       'interviews.tab.education',
    placeholderKey: 'interviews.educationPlaceholder',
  },
  {
    id:             'interviewRealEstate',
    labelKey:       'interviews.tab.realEstate',
    placeholderKey: 'interviews.realEstatePlaceholder',
  },
];

// ─── Main component ──────────────────────────────────────────────────────────

export const CommercialInterviews = () => {
  const [activeTab, setActiveTab]       = useState('interviewDoctor');
  const [guestName, setGuestName]       = useState('');
  const [orgName, setOrgName]           = useState('');
  const [designation, setDesignation]   = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [includeWebSearch, setIncludeWebSearch] = useState(true);

  const [loadingStage, setLoadingStage] = useState(null); // null | 'searching' | 'generating'
  const [error, setError]               = useState('');
  const [sections, setSections]         = useState([]); // [{ type, questions: [] }]
  const [rawOutput, setRawOutput]       = useState('');
  const [openSections, setOpenSections] = useState(new Set());
  const [copiedSection, setCopiedSection] = useState(null);
  const [copiedAll, setCopiedAll]       = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [showTooltip, setShowTooltip]   = useState(false);

  const outputRef   = useRef(null);
  const downloadRef = useRef(null);

  const tab = TABS.find((t) => t.id === activeTab);

  // Close download menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setSections([]);
    setRawOutput('');
    setError('');
    setOpenSections(new Set());
  };

  const handleGenerate = async () => {
    if (!guestName.trim()) {
      setError('Please enter the guest name before generating.');
      return;
    }
    setSections([]);
    setRawOutput('');
    setError('');
    setOpenSections(new Set());

    try {
      // Show searching stage if web search is enabled
      if (includeWebSearch && orgName.trim()) {
        setLoadingStage('searching');
        await new Promise((r) => setTimeout(r, 400)); // let the UI paint
      }
      setLoadingStage('generating');

      const result = await interviewService.generateInterview({
        interviewType:    activeTab,
        guestName:        guestName.trim(),
        organizationName: orgName.trim(),
        designation:      designation.trim(),
        additionalInfo:   additionalInfo.trim(),
        includeWebSearch,
        language:         'telugu',
      });

      const parsed = result?.sections || [];
      setSections(parsed);
      setRawOutput(result?.rawOutput || '');
      // Open all sections by default
      setOpenSections(new Set(parsed.map((_, i) => i)));

      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch (e) {
      setError(
        e.response?.data?.message ||
        e.message ||
        'Generation failed. Check your AI provider settings.'
      );
    } finally {
      setLoadingStage(null);
    }
  };

  const toggleSection = (index) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleCopySection = async (section, index) => {
    const text = `${section.type}\n${section.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(index);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (_) {
      setError('Clipboard access denied by browser.');
    }
  };

  const handleCopyAll = async () => {
    if (!sections.length) return;
    const text = sections
      .map((s) => `${s.type}\n${s.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (_) {
      setError('Clipboard access denied by browser.');
    }
  };

  const handleDownload = async (format) => {
    if (!sections.length) return;
    setDownloadOpen(false);
    const flatText = sections
      .map((s) => `${s.type}\n${s.questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`)
      .join('\n\n');
    const title = `Interview Questions — ${guestName} — ${orgName || tab?.label}`;
    try {
      const blob = await contentService.downloadContent({ text: flatText, title, format });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `interview-${guestName.replace(/\s+/g, '-').toLowerCase()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) {
      setError('Download failed. Please try again.');
    }
  };

  const handleClear = () => {
    setGuestName('');
    setOrgName('');
    setDesignation('');
    setAdditionalInfo('');
    setSections([]);
    setRawOutput('');
    setError('');
    setOpenSections(new Set());
  };

  const isLoading = loadingStage !== null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiUsers size={20} className="text-primary-600" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Commercial Interviews</h1>
          <p className="text-sm text-gray-500">
            AI-generated structured question sets for sponsored on-air guest interviews
          </p>
        </div>
      </div>

      {/* Tab bar — mobile (< 768px): native <select> dropdown.
                   Tablet+ (>= 768px): horizontal tab bar with overflow-x scroll. */}
      <div className="md:hidden">
        <select
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          className="input w-full text-sm font-medium"
          aria-label="Select interview type"
        >
          {TABS.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>
      <div
        className="hidden md:flex border-b border-gray-200 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => handleTabChange(t.id)}
            className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === t.id
                ? 'border-primary-500 text-primary-600 bg-primary-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

        {/* ── Input form — fields full width on mobile, 2-column grid on
               desktop (FIX F, item 11) for Guest Name / Org / Designation. ──── */}
        <div className="space-y-3">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Guest Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Dr. Ravi Kumar / Prof. Sunita Reddy / Mr. Arun Sharma"
                className="input w-full text-sm"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                {activeTab === 'interviewDoctor'
                  ? 'Hospital / Clinic Name'
                  : activeTab === 'interviewEducation'
                  ? 'University / Institution Name'
                  : 'Company / Project Name'}
              </label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder={
                  activeTab === 'interviewDoctor'
                    ? 'NIMS Hospital, Hyderabad'
                    : activeTab === 'interviewEducation'
                    ? 'Osmania University, Hyderabad'
                    : 'Rainbow Infracon, Hyderabad'
                }
                className="input w-full text-sm"
                disabled={isLoading}
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Designation / Title
              </label>
              <input
                type="text"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                placeholder={
                  activeTab === 'interviewDoctor'
                    ? 'Senior Cardiologist & Head of Department'
                    : activeTab === 'interviewEducation'
                    ? 'Vice Chancellor / Principal'
                    : 'MD & Managing Director'
                }
                className="input w-full text-sm"
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Additional Info
            </label>
            <textarea
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder={tab?.placeholder}
              className="input w-full resize-y text-sm font-sans leading-relaxed"
              style={{ minHeight: '100px' }}
              disabled={isLoading}
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Anything you want the AI to know — awards, controversies, recent news, focus areas.
            </p>
          </div>

          {/* Web search toggle */}
          <div className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-gray-50 border border-gray-200">
            <FiGlobe size={14} className={includeWebSearch ? 'text-primary-500' : 'text-gray-400'} />
            <label className="flex items-center gap-2 cursor-pointer flex-1 select-none" htmlFor="web-toggle">
              <span className="text-sm font-medium text-gray-700">Include web research</span>
              <div className="relative inline-flex items-center">
                <input
                  id="web-toggle"
                  type="checkbox"
                  checked={includeWebSearch}
                  onChange={(e) => setIncludeWebSearch(e.target.checked)}
                  className="sr-only peer"
                  disabled={isLoading}
                />
                <div
                  onClick={() => !isLoading && setIncludeWebSearch((v) => !v)}
                  className={`w-9 h-5 rounded-full cursor-pointer transition-colors ${
                    includeWebSearch ? 'bg-primary-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      includeWebSearch ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </div>
              </div>
            </label>
            <div className="relative">
              <button
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiInfo size={13} />
              </button>
              {showTooltip && (
                <div className="absolute right-0 bottom-6 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-30 leading-relaxed">
                  Searches the web for background info on the guest and organisation before generating questions.
                  Uses SerpAPI if configured, or DuckDuckGo as fallback (free, no key needed).
                </div>
              )}
            </div>
          </div>

          {/* Action row */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="btn btn-primary flex items-center gap-2 min-w-[160px] justify-center"
            >
              {isLoading ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {loadingStage === 'searching'
                    ? `Researching${orgName ? ` ${orgName}` : ''}…`
                    : 'Generating…'}
                </>
              ) : (
                <>
                  <FiZap size={14} />
                  Generate Questions
                </>
              )}
            </button>
            <button
              onClick={handleClear}
              disabled={isLoading}
              className="btn btn-ghost text-sm text-gray-500 flex items-center gap-1.5 disabled:opacity-40"
            >
              <FiX size={14} />
              Clear
            </button>
          </div>

          {/* Error banner */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
              <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => setError('')} className="text-danger-400 hover:text-danger-600">
                <FiX size={12} />
              </button>
            </div>
          )}
        </div>

        {/* ── Output: accordion + actions ────────────────────────────────── */}
        <div ref={outputRef} className="space-y-3">

          {/* Loading placeholder */}
          {isLoading && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-primary-600 bg-gray-50">
                <div className="w-9 h-9 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
                <div className="text-sm font-semibold text-center">
                  {loadingStage === 'searching' ? (
                    <>
                      <p>Researching{orgName ? ` ${orgName}` : ''} online…</p>
                      <p className="text-xs text-gray-400 font-normal mt-1">
                        Gathering background context from the web
                      </p>
                    </>
                  ) : (
                    <>
                      <p>Generating questions…</p>
                      <p className="text-xs text-gray-400 font-normal mt-1">
                        AI is writing 50 interview questions across 10 topics
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && sections.length === 0 && (
            <div className="border border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
              <FiUsers size={36} className="text-gray-200" />
              <p className="text-sm font-medium">50+ questions will appear here</p>
              <p className="text-xs text-gray-400 text-center px-6">
                Fill in the guest details on the left and click Generate
              </p>
            </div>
          )}

          {/* Sections accordion */}
          {!isLoading && sections.length > 0 && (
            <>
              {/* Header bar with actions */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm font-semibold text-gray-700">
                  {sections.length} sections · {sections.reduce((n, s) => n + s.questions.length, 0)} questions
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleCopyAll}
                    className="btn btn-ghost flex items-center gap-1.5 text-sm"
                  >
                    {copiedAll
                      ? <FiCheck size={13} className="text-green-500" />
                      : <FiCopy size={13} />}
                    <span>{copiedAll ? 'Copied!' : 'Copy All'}</span>
                  </button>

                  <div className="relative" ref={downloadRef}>
                    <button
                      onClick={() => setDownloadOpen((v) => !v)}
                      className="btn btn-ghost flex items-center gap-1.5 text-sm"
                    >
                      <FiDownload size={13} />
                      <span>Download</span>
                      <FiChevronDown
                        size={11}
                        className={`text-gray-400 transition-transform ${downloadOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {downloadOpen && (
                      <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20">
                        <button
                          onClick={() => handleDownload('docx')}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                          <FiFileText size={13} className="text-blue-500 flex-shrink-0" />
                          <span>.docx (Word)</span>
                        </button>
                        <button
                          onClick={() => handleDownload('pdf')}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                          <FiFile size={13} className="text-red-500 flex-shrink-0" />
                          <span>.pdf</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Accordion */}
              <div className="space-y-2">
                {sections.map((section, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    {/* Section header */}
                    <div className="flex items-center bg-gray-50 hover:bg-gray-100 transition">
                      <button
                        onClick={() => toggleSection(idx)}
                        className="flex-1 flex items-center gap-2.5 px-4 sm:px-5 py-3 text-left"
                      >
                        <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-semibold text-gray-800 flex-1">
                          {section.type}
                        </span>
                        <span className="text-xs text-gray-400 mr-1">
                          {section.questions.length} questions
                        </span>
                        {openSections.has(idx)
                          ? <FiChevronUp size={14} className="text-gray-400 flex-shrink-0" />
                          : <FiChevronDown size={14} className="text-gray-400 flex-shrink-0" />}
                      </button>
                      <button
                        onClick={() => handleCopySection(section, idx)}
                        title="Copy this section"
                        className="px-3 py-3 text-gray-400 hover:text-primary-600 transition border-l border-gray-200"
                      >
                        {copiedSection === idx
                          ? <FiCheck size={13} className="text-green-500" />
                          : <FiCopy size={13} />}
                      </button>
                    </div>

                    {/* Questions list */}
                    {openSections.has(idx) && (
                      <div className="px-4 sm:px-5 py-3 sm:py-4 space-y-2 border-t border-gray-100 bg-white">
                        {section.questions.map((q, qi) => (
                          <div key={qi} className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
                            <span className="w-5 h-5 rounded flex-shrink-0 text-[11px] font-bold text-primary-500 mt-0.5">
                              Q{qi + 1}
                            </span>
                            <span>{q}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
